import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Popover,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  PhoneIphone as MobileIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import {
  BonCommande,
  BonCommandeCreate,
  BonCommandeUpdate,
  BonCommandeItemCreate,
} from '../../types/bonCommande';
import { DemandeAchat } from '../../types/demandeAchat';
import { Article } from '../../types';
import { Supplier } from '../../types/supplier';
import { ConditionPaiement } from '../../types/conditionPaiement';
import { ModePaiement } from '../../types/modePaiement';
import {
  bonsCommandeAPI,
  demandesAchatAPI,
  articlesAPI,
  suppliersAPI,
  conditionsPaiementAPI,
  modesPaiementAPI,
} from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';

interface LineItem {
  article_id: number | null;
  designation: string;
  quantite: number | null | string;
  prix_unitaire: number | null | string;
  total: number;
}

interface BonCommandeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bonCommandeId?: number;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const STOCK_COLOR = '#2196F3';
const BC_TVA_RATE = 20; // Fixed TVA for all Bons de Commande

// Round to 2 decimals (avoid float arithmetic garbage like 0.1 + 0.2 = 0.30000004)
const round2 = (n: number): number => Math.round(n * 100) / 100;

// French money formatter — always exactly 2 decimals (caps the display, no float spillover)
const formatMoney = (n: number): string =>
  n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyItem = (): LineItem => ({
  article_id: null,
  designation: '',
  quantite: null,
  prix_unitaire: null,
  total: 0,
});

const BonCommandeFormDialog: React.FC<BonCommandeFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  bonCommandeId,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const isEdit = !!bonCommandeId;

  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);

  // Dropdown data
  const [articles, setArticles] = useState<Article[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [demandesAchat, setDemandesAchat] = useState<DemandeAchat[]>([]);
  const [conditions, setConditions] = useState<ConditionPaiement[]>([]);
  const [modes, setModes] = useState<ModePaiement[]>([]);

  // Selected values
  const [selectedDA, setSelectedDA] = useState<DemandeAchat | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<ConditionPaiement | null>(null);
  const [selectedMode, setSelectedMode] = useState<ModePaiement | null>(null);
  const [remarque, setRemarque] = useState('');
  const [supplierDevisRef, setSupplierDevisRef] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([emptyItem()]);

  // Reference preview
  const [nextReference, setNextReference] = useState<string>('');
  const [editingReference, setEditingReference] = useState<string>('');

  // Supplier info popover
  const [supplierInfoAnchor, setSupplierInfoAnchor] = useState<HTMLButtonElement | null>(null);

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new bons de commande (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    date: string;
    selectedDA: DemandeAchat | null;
    selectedSupplier: Supplier | null;
    selectedCondition: ConditionPaiement | null;
    selectedMode: ModePaiement | null;
    remarque: string;
    supplierDevisRef: string;
    lineItems: LineItem[];
  }>('bon-commande', !isEdit);

  // Reset everything on close
  const resetForm = () => {
    setDate(new Date().toISOString().split('T')[0]);
    setSelectedDA(null);
    setSelectedSupplier(null);
    setSelectedCondition(null);
    setSelectedMode(null);
    setRemarque('');
    setSupplierDevisRef('');
    setLineItems([emptyItem()]);
    setEditingReference('');
  };

  // Load dropdowns when dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [articlesData, suppliersData, condsData, modesData] = await Promise.all([
          articlesAPI.getAllForDropdown(),
          suppliersAPI.getAllForDropdown(),
          conditionsPaiementAPI.getAll(),
          modesPaiementAPI.getAll(),
        ]);
        setArticles(articlesData);
        setSuppliers(suppliersData);
        setConditions(condsData);
        setModes(modesData);
      } catch (err) {
        console.error('Erreur chargement dropdowns', err);
      }
    })();
  }, [open]);

  // Load DAs (validated + unlinked) — refetched whenever the dialog opens or we're editing
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await demandesAchatAPI.getPaginated(0, 500, undefined, {
          statuses: ['valide'],
          unlinked: true,
        });
        setDemandesAchat(res.items);
      } catch (err) {
        console.error('Erreur chargement DA', err);
      }
    })();
  }, [open]);

  // Load existing BC when editing, OR reset for create
  useEffect(() => {
    if (!open) return;
    if (!bonCommandeId) {
      const draft = loadDraft();
      if (draft) {
        setDate(draft.date);
        setSelectedDA(draft.selectedDA);
        setSelectedSupplier(draft.selectedSupplier);
        setSelectedCondition(draft.selectedCondition);
        setSelectedMode(draft.selectedMode);
        setRemarque(draft.remarque);
        setSupplierDevisRef(draft.supplierDevisRef);
        setLineItems(draft.lineItems);
        setEditingReference('');
      } else {
        resetForm();
      }
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const bc: BonCommande = await bonsCommandeAPI.getById(bonCommandeId);
        setDate(bc.date);
        setEditingReference(bc.reference);
        setRemarque(bc.remarque || '');
        setSupplierDevisRef(bc.supplier_devis_ref || '');
        // The selected DA might be in en_cours_livraison status (not in the unlinked list),
        // so resolve it directly via getById to ensure it's selectable
        try {
          const linkedDA = await demandesAchatAPI.getById(bc.demande_achat_id);
          setSelectedDA(linkedDA);
          // Make sure it appears in the dropdown options
          setDemandesAchat((prev) =>
            prev.find((d) => d.id === linkedDA.id) ? prev : [linkedDA, ...prev],
          );
        } catch {
          /* ignore */
        }
        setLineItems(
          bc.items.length === 0
            ? [emptyItem()]
            : bc.items.map((it) => ({
                article_id: it.article_id ?? null,
                designation: it.designation,
                quantite: typeof it.quantite === 'string' ? parseFloat(it.quantite) : it.quantite,
                prix_unitaire:
                  typeof it.prix_unitaire === 'string' ? parseFloat(it.prix_unitaire) : it.prix_unitaire,
                total: typeof it.total === 'string' ? parseFloat(it.total) : it.total,
              })),
        );
      } catch (err: any) {
        showError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, bonCommandeId]);

  // Resolve selected supplier / condition / mode after dropdowns load (edit mode)
  useEffect(() => {
    if (!isEdit || !bonCommandeId) return;
    if (suppliers.length === 0) return;
    (async () => {
      try {
        const bc = await bonsCommandeAPI.getById(bonCommandeId);
        setSelectedSupplier(suppliers.find((s) => s.id === bc.supplier_id) || null);
        if (bc.condition_paiement_id) {
          setSelectedCondition(conditions.find((c) => c.id === bc.condition_paiement_id) || null);
        }
        if (bc.mode_paiement_id) {
          setSelectedMode(modes.find((m) => m.id === bc.mode_paiement_id) || null);
        }
      } catch {
        /* already alerted by main edit loader */
      }
    })();
  }, [isEdit, bonCommandeId, suppliers, conditions, modes]);

  // Fetch next reference preview (create mode only)
  useEffect(() => {
    if (!open || isEdit || !date) {
      setNextReference('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await bonsCommandeAPI.getNextReference(date);
        if (!cancelled) setNextReference(res.reference);
      } catch (err) {
        if (!cancelled) setNextReference('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, date]);

  // Auto-fill items when DA is selected (create mode only — don't overwrite items in edit)
  const handleDAChange = (newDA: DemandeAchat | null) => {
    setSelectedDA(newDA);
    if (!isEdit && newDA && newDA.items && newDA.items.length > 0) {
      setLineItems(
        newDA.items.map((it) => ({
          article_id: it.article_id ?? null,
          designation: it.designation,
          quantite: typeof it.quantite === 'string' ? parseFloat(it.quantite) : it.quantite,
          prix_unitaire: null,
          total: 0,
        })),
      );
    }
  };

  const handleItemChange = (index: number, field: keyof LineItem, value: any) => {
    setLineItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      // Recalculate total — coerce to numbers regardless of input type
      const rawQte = next[index].quantite;
      const rawPu = next[index].prix_unitaire;
      const qte =
        typeof rawQte === 'string' ? parseFloat(rawQte.replace(',', '.')) : (rawQte ?? NaN);
      const pu =
        typeof rawPu === 'string' ? parseFloat(rawPu.replace(',', '.')) : (rawPu ?? NaN);
      if (!isNaN(qte) && !isNaN(pu) && qte > 0 && pu >= 0) {
        next[index].total = Math.round(qte * pu * 100) / 100;
      } else {
        next[index].total = 0;
      }
      return next;
    });
  };

  const handleArticleSelect = (index: number, article: Article) => {
    setLineItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        article_id: article.id,
        designation: article.designation,
      };
      return next;
    });
  };

  const addLineItem = () => setLineItems((prev) => [...prev, emptyItem()]);

  const removeLineItem = (index: number) => {
    if (lineItems.length === 1) return;
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Sum and round to 2 decimals first (kills float arithmetic noise),
  // then derive TTC by direct multiplication so HT + TVA = TTC always holds.
  const grandTotal = round2(lineItems.reduce((sum, it) => sum + (it.total || 0), 0));
  const totalTTC = round2(grandTotal * (1 + BC_TVA_RATE / 100));
  const tvaAmount = round2(totalTTC - grandTotal);

  const handleSubmit = async () => {
    if (!date) {
      showError('Veuillez sélectionner une date');
      return;
    }
    if (!selectedDA) {
      showError('Veuillez sélectionner une demande d\'achat');
      return;
    }
    if (!selectedSupplier) {
      showError('Veuillez sélectionner un fournisseur');
      return;
    }

    const validItems: BonCommandeItemCreate[] = [];
    for (const item of lineItems) {
      const qte =
        typeof item.quantite === 'string'
          ? parseFloat(item.quantite.replace(',', '.'))
          : item.quantite;
      const pu =
        typeof item.prix_unitaire === 'string'
          ? parseFloat(item.prix_unitaire.replace(',', '.'))
          : item.prix_unitaire;
      if (item.designation && item.designation.trim() !== '' && qte && qte > 0 && pu !== null && pu >= 0) {
        validItems.push({
          article_id: item.article_id,
          designation: item.designation.trim(),
          quantite: qte,
          prix_unitaire: pu,
          total: Math.round(qte * pu * 100) / 100,
        });
      }
    }

    if (validItems.length === 0) {
      showError('Veuillez ajouter au moins un article avec quantité et prix unitaire valides');
      return;
    }

    try {
      setLoading(true);
      if (isEdit && bonCommandeId) {
        const payload: BonCommandeUpdate = {
          date,
          demande_achat_id: selectedDA.id,
          supplier_id: selectedSupplier.id,
          condition_paiement_id: selectedCondition?.id || null,
          mode_paiement_id: selectedMode?.id || null,
          remarque: remarque.trim() || null,
          supplier_devis_ref: supplierDevisRef.trim() || null,
          items: validItems,
        };
        await bonsCommandeAPI.update(bonCommandeId, payload);
        showSuccess('Bon de commande mis à jour avec succès');
      } else {
        const payload: BonCommandeCreate = {
          date,
          demande_achat_id: selectedDA.id,
          supplier_id: selectedSupplier.id,
          condition_paiement_id: selectedCondition?.id || null,
          mode_paiement_id: selectedMode?.id || null,
          remarque: remarque.trim() || null,
          supplier_devis_ref: supplierDevisRef.trim() || null,
          items: validItems,
        };
        await bonsCommandeAPI.create(payload);
        showSuccess('Bon de commande créé avec succès');
      }
      clearDraft();
      onSuccess();
      onClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // X / ANNULER = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({
        date,
        selectedDA,
        selectedSupplier,
        selectedCondition,
        selectedMode,
        remarque,
        supplierDevisRef,
        lineItems,
      });
      onClose();
    }
  };

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ bgcolor: STOCK_COLOR, color: 'white', py: 1.25 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {isEdit ? 'Modifier le bon de commande' : 'Nouveau bon de commande'}
              {(isEdit ? editingReference : nextReference) && (
                <Typography component="span" variant="caption" sx={{ ml: 1.5, color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: 0.5 }}>
                  N° BC: {isEdit ? editingReference : nextReference}
                </Typography>
              )}
            </Typography>
            <Box display="flex" alignItems="center">
              <MinimizeButton color="white" onClick={minimize} />
              <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {/* Header row */}
          <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={demandesAchat}
                value={selectedDA}
                onChange={(_, val) => handleDAChange(val)}
                getOptionLabel={(opt) => opt.reference}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label="DA N° *" size="small" required />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.reference}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.user_name} — {option.total_items} article{option.total_items > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                size="small"
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Autocomplete
                  options={suppliers}
                  value={selectedSupplier}
                  onChange={(_, val) => setSelectedSupplier(val)}
                  getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Fournisseur *" size="small" required />
                  )}
                  size="small"
                  fullWidth
                />
                <Tooltip title="Détails du fournisseur">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!selectedSupplier}
                      onClick={(e) => setSupplierInfoAnchor(e.currentTarget)}
                      sx={{ color: STOCK_COLOR }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

          {/* Conditions / Modes / Réf. devis fournisseur row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={conditions}
                value={selectedCondition}
                onChange={(_, val) => setSelectedCondition(val)}
                getOptionLabel={(opt) => opt.nom}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label="Conditions de paiement" size="small" />
                )}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={modes}
                value={selectedMode}
                onChange={(_, val) => setSelectedMode(val)}
                getOptionLabel={(opt) => opt.nom}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label="Mode de paiement" size="small" />
                )}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Réf. devis fournisseur"
                value={supplierDevisRef}
                onChange={(e) => setSupplierDevisRef(e.target.value)}
                fullWidth
                size="small"
                placeholder="Ex: DV-2026-001"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Items table */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ color: STOCK_COLOR, fontWeight: 600 }}>
              Articles commandés
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addLineItem}
              sx={{ borderColor: STOCK_COLOR, color: STOCK_COLOR }}
            >
              Ajouter un article
            </Button>
          </Box>

          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ width: '40%' }}>Désignation</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Quantité</TableCell>
                  <TableCell sx={{ width: '20%' }} align="right">Prix unitaire</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Total</TableCell>
                  <TableCell sx={{ width: '10%' }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={articles}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return `${option.reference} - ${option.designation}`;
                        }}
                        value={
                          item.article_id
                            ? articles.find((a) => a.id === item.article_id) || null
                            : item.designation || ''
                        }
                        onChange={(_, value) => {
                          if (typeof value === 'string') {
                            handleItemChange(index, 'designation', value);
                            handleItemChange(index, 'article_id', null);
                          } else if (value) {
                            handleArticleSelect(index, value);
                          } else {
                            handleItemChange(index, 'designation', '');
                            handleItemChange(index, 'article_id', null);
                          }
                        }}
                        onBlur={(e) => {
                          const inputValue = (e.target as HTMLInputElement).value;
                          if (inputValue && !item.article_id && inputValue !== item.designation) {
                            handleItemChange(index, 'designation', inputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            multiline
                            rows={2}
                            placeholder="Sélectionner ou saisir"
                            sx={{ '& .MuiInputBase-root': { alignItems: 'flex-start' } }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Typography variant="body2">
                              {option.reference} - {option.designation}
                            </Typography>
                          </li>
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        size="small"
                        value={item.quantite ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleItemChange(index, 'quantite', null);
                          } else if (/^\d*[.,]?\d*$/.test(value)) {
                            handleItemChange(index, 'quantite', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && value !== '') {
                            const num = parseFloat(value.replace(',', '.'));
                            handleItemChange(index, 'quantite', isNaN(num) ? null : num);
                          }
                        }}
                        fullWidth
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        size="small"
                        value={item.prix_unitaire ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleItemChange(index, 'prix_unitaire', null);
                          } else if (/^\d*[.,]?\d*$/.test(value)) {
                            handleItemChange(index, 'prix_unitaire', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && value !== '') {
                            const num = parseFloat(value.replace(',', '.'));
                            handleItemChange(index, 'prix_unitaire', isNaN(num) ? null : num);
                          }
                        }}
                        fullWidth
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(item.total)} DH
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Grand total + remarque */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Remarque"
                value={remarque}
                onChange={(e) => setRemarque(e.target.value)}
                fullWidth
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: STOCK_COLOR,
                  borderRadius: 1,
                  bgcolor: 'rgba(33, 150, 243, 0.06)',
                  textAlign: 'right',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">Total H.T</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatMoney(grandTotal)} DH
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">T.V.A {BC_TVA_RATE}%</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatMoney(tvaAmount)} DH
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}>
                  <Typography variant="caption" sx={{ color: STOCK_COLOR, fontWeight: 700 }}>Total T.T.C</Typography>
                  <Typography variant="h6" sx={{ color: STOCK_COLOR, fontWeight: 700 }}>
                    {formatMoney(totalTTC)} DH
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{ bgcolor: STOCK_COLOR, '&:hover': { bgcolor: '#1E88E5' } }}
          >
            {isEdit ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color={STOCK_COLOR}
        title={`${isEdit ? 'Modifier le bon de commande' : 'Nouveau bon de commande'}${
          (isEdit ? editingReference : nextReference)
            ? ` — ${isEdit ? editingReference : nextReference}`
            : ''
        }`}
        onRestore={restore}
        onClose={handleClose}
      />

      {/* Supplier info popover */}
      <Popover
        open={Boolean(supplierInfoAnchor)}
        anchorEl={supplierInfoAnchor}
        onClose={() => setSupplierInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {selectedSupplier && (
          <Box sx={{ p: 2, minWidth: 280, maxWidth: 360 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: STOCK_COLOR, mb: 1 }}>
              {selectedSupplier.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Code: {selectedSupplier.code}
            </Typography>
            {selectedSupplier.ice && (
              <Typography variant="caption" color="text.secondary" display="block">
                ICE: {selectedSupplier.ice}
              </Typography>
            )}
            {selectedSupplier.address && (
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                {selectedSupplier.address}
              </Typography>
            )}
            {!selectedSupplier.address && <Box sx={{ mb: 1.5 }} />}
            {selectedSupplier.contact_name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2">{selectedSupplier.contact_name}</Typography>
              </Box>
            )}
            {selectedSupplier.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2">{selectedSupplier.email}</Typography>
              </Box>
            )}
            {selectedSupplier.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="body2">{selectedSupplier.phone}</Typography>
              </Box>
            )}
            {selectedSupplier.mobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MobileIcon fontSize="small" color="action" />
                <Typography variant="body2">{selectedSupplier.mobile}</Typography>
              </Box>
            )}
            {!selectedSupplier.contact_name && !selectedSupplier.email && !selectedSupplier.phone && !selectedSupplier.mobile && (
              <Typography variant="caption" color="text.secondary">Aucune information de contact</Typography>
            )}
          </Box>
        )}
      </Popover>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />
    </>
  );
};

export default BonCommandeFormDialog;
